export interface ReplayStatus {
  active: boolean;
  paused: boolean;
  rate_level: number;
  current_time: number;
  loaded_start: number;
  loaded_end: number;
  /** A window load / replay start is in flight. The web ReplayLoadDialog polls
   *  this to show the "Loading replay data…" / "Starting replay…" progress the
   *  desktop drives off Tauri events. (Optional for older/desktop payloads.) */
  loading?: boolean;
  /** When `loading`, this is a Start (arming) rather than a window load. */
  starting?: boolean;
  /** Turbine being loaded/replayed, for the progress dialog title. */
  config?: string;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

async function call<T = void>(cmd: string, args?: Record<string, unknown>): Promise<T | null> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

// Web (browser) equivalents of the Tauri replay commands. Route names match the
// webserver mirror (see webserver.rs). GET for all (the player commands take no
// body); query params for the two that carry args.
async function web<T = void>(path: string, parseJson = false): Promise<T | null> {
  try {
    const r = await fetch(path);
    if (!r.ok) return null;
    return parseJson ? ((await r.json()) as T) : (null as T | null);
  } catch {
    return null;
  }
}

export const getReplayStatus = () =>
  isTauri() ? call<ReplayStatus>("get_replay_status")
            : web<ReplayStatus>("/api/get_replay_status", true);
export const replayStart = () => isTauri() ? call("replay_start") : web("/api/replay_start");
export const replayPlay = () => isTauri() ? call("replay_play") : web("/api/replay_play");
export const replayPause = () => isTauri() ? call("replay_pause") : web("/api/replay_pause");
export const replayStep = () => isTauri() ? call("replay_step") : web("/api/replay_step");
export const replayStop = () => isTauri() ? call("replay_stop") : web("/api/replay_stop");
export const replayJump = (forward: boolean) =>
  isTauri() ? call("replay_jump", { forward }) : web(`/api/replay_jump?forward=${forward}`);
export const replayLoadPrev = () => isTauri() ? call("replay_load_prev") : web("/api/replay_load_prev");
export const replayLoadNext = () => isTauri() ? call("replay_load_next") : web("/api/replay_load_next");
export const replaySetRate = (level: number) =>
  isTauri() ? call("replay_set_rate", { level }) : web(`/api/replay_set_rate?level=${level}`);
